import { useState, useEffect, useCallback } from 'react';
import { data_from_server_post } from '@/shared/http/client';
/**
 * Custom hook to manage manual model predictions state
 * Fetches available options and current selection for a usecase
 */
export const useManualModelPredictions = (currentUsecase) => {
    const [manualPredictionOptions, setManualPredictionOptions] = useState([]);
    const [selectedManualPredictionDatasets, setSelectedManualPredictionDatasets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const setSelectedIfChanged = useCallback((chosen) => {
        const normalized = Array.from(new Set((chosen || []).filter(Boolean))).sort();
        setSelectedManualPredictionDatasets((prev) => {
            const prevSorted = [...(prev || [])].filter(Boolean).sort();
            if (
                prevSorted.length === normalized.length &&
                prevSorted.every((v, idx) => v === normalized[idx])
            ) {
                return prev;
            }
            return normalized;
        });
    }, []);

    // Refresh manual predictions options (for all client's datasets) and the current chosen datasets for this task
    const refreshManualPredictionsState = useCallback(() => {
        if (!currentUsecase) return;
        setIsLoading(true);
        const clientId = currentUsecase.client_id;
        const usecaseId = currentUsecase._id;

        data_from_server_post(
            "manual-model-predictions-info",
            clientId,
            (result) => {
                const infos = Array.isArray(result?.datasets_info) ? result.datasets_info : [];
                const options = infos.map(i => ({
                    id: i.dataset,
                    display_name: i.dataset,
                    last_update_time: i.last_update_time,
                }));
                const sortedOptions = options
                    .slice()
                    .sort((a, b) => new Date(b.last_update_time) - new Date(a.last_update_time));
                setManualPredictionOptions(sortedOptions);

                // Fetch the latest usecase to update the chosen external files.
                // IMPORTANT: do NOT derive selection from `datasets_info` because dataset names can collide
                // across sources (e.g., benchmark + external predictions with same name).
                data_from_server_post(
                    "llm_usecase_info",
                    { client_id: clientId, llm_usecase_id: usecaseId },
                    (res) => {
                        const usecase = res?.llm_usecase || {};
                        const chosen = Array.isArray(usecase?.manual_model_predictions)
                            ? usecase.manual_model_predictions
                            : [];
                        setSelectedIfChanged(chosen);
                        setIsLoading(false);
                    },
                    (error_message) => {
                        console.warn("Failed to load llm_usecase_info:", error_message);
                        setIsLoading(false);
                    }
                );
            },
            (error_message) => {
                console.warn("Failed to load manual model predictions options:", error_message);
                setIsLoading(false);
            }
        );
    }, [currentUsecase, setSelectedIfChanged]);

    // Initial load
    useEffect(() => {
        if (!currentUsecase) return;
        refreshManualPredictionsState();
    }, [currentUsecase, refreshManualPredictionsState]);

    // Keep local selection in sync with the provided usecase (fast UI updates on refresh).
    useEffect(() => {
        if (!currentUsecase) return;
        if (Array.isArray(currentUsecase?.manual_model_predictions)) {
            setSelectedIfChanged(currentUsecase.manual_model_predictions);
        }
    }, [currentUsecase?.manual_model_predictions, currentUsecase, setSelectedIfChanged]);

    return {
        manualPredictionOptions,
        selectedManualPredictionDatasets,
        setSelectedManualPredictionDatasets,
        refreshManualPredictionsState,
        isLoading
    };
};

